"use client";

import React, { useState, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import { HomeBanner } from "@/packages/types/banner";
import PromoCardV2 from "./PromoCardV2";
import HeroDots from "./HeroDots";
import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";

import "swiper/css";

interface PromoCardSliderV2Props {
  promoCards: HomeBanner[];
  className?: string;
}

const PromoCardSliderV2: React.FC<PromoCardSliderV2Props> = ({
  promoCards,
  className = "",
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
  }, []);

  const handleDotClick = useCallback(
    (index: number) => {
      swiperInstance?.slideToLoop(index);
    },
    [swiperInstance]
  );

  const handleMouseEnter = useCallback(() => {
    if (swiperInstance?.autoplay && swiperInstance.autoplay.running) {
      swiperInstance.autoplay.stop();
    }
  }, [swiperInstance]);

  const handleMouseLeave = useCallback(() => {
    if (swiperInstance?.autoplay && !swiperInstance.autoplay.running) {
      swiperInstance.autoplay.start();
    }
  }, [swiperInstance]);

  if (!promoCards || promoCards.length === 0) return null;

  // Single card display (no slider overhead needed)
  if (promoCards.length === 1) {
    const card = promoCards[0];
    return (
      <PromoCardV2
        imageUrl={card.imageUrl}
        mobileUrl={card.mobileUrl}
        navigationLink={card.navigationLink}
        className={className}
      />
    );
  }

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative w-full h-full overflow-hidden group select-none ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card} ${HEADER_V2_DESIGN_SYSTEM.elevation.card} ${HEADER_V2_DESIGN_SYSTEM.elevation.cardHover} ${className}`}
    >
      <Swiper
        modules={[Autoplay, Keyboard]}
        slidesPerView={1}
        spaceBetween={0}
        loop={true}
        keyboard={{ enabled: true }}
        autoplay={{ delay: 4500, disableOnInteraction: false }}
        onSwiper={setSwiperInstance}
        onSlideChange={handleSlideChange}
        className="w-full h-full"
      >
        {promoCards.map((card, index) => (
          <SwiperSlide key={card.id || index} className="w-full h-full">
            <PromoCardV2
              imageUrl={card.imageUrl}
              mobileUrl={card.mobileUrl}
              navigationLink={card.navigationLink}
              className="w-full h-full !shadow-none !border-none !rounded-none"
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Pagination Dots (Shown when more than 1 promo card exists) */}
      <HeroDots
        total={promoCards.length}
        activeIndex={activeIndex}
        onDotClick={handleDotClick}
      />
    </div>
  );
};

export default React.memo(PromoCardSliderV2);
